// The admin API client: same-origin /api/admin/* (Cloudflare Access sits in
// front of the whole admin host, so the browser carries the Access cookie
// and the API sees the verified identity). Shapes:
// packages/domain/src/views/admin.ts.

import { useCallback, useEffect, useRef, useState } from "react";

export class AdminApiError extends Error {
  constructor(
    readonly route: string,
    readonly status: number,
    message: string,
    readonly requestId: string | null,
    readonly at: Date,
  ) {
    super(message);
  }
}

export async function adminGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const route = `/api/admin/${path}`;
  let res: Response;
  try {
    res = await fetch(route, { credentials: "same-origin", headers: { accept: "application/json" }, ...(signal ? { signal } : {}) });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new AdminApiError(route, 0, "The admin API couldn't be reached.", null, new Date());
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body.error === "string") message = body.error;
    } catch {
      /* not JSON — keep the status text */
    }
    throw new AdminApiError(route, res.status, message, res.headers.get("cf-ray"), new Date());
  }
  return (await res.json()) as T;
}

export interface Query<T> {
  data: T | null;
  error: AdminApiError | null;
  loading: boolean;
  /** When the current data arrived. */
  updatedAt: Date | null;
  reload: () => void;
}

/**
 * GET `path` whenever it changes (null = don't fetch). Keeps the previous
 * data while a new request is in flight, so range switches don't flash the
 * skeleton; `pollMs` refreshes in the background.
 */
export function useAdminQuery<T>(path: string | null, pollMs?: number): Query<T> {
  const [state, setState] = useState<{ data: T | null; error: AdminApiError | null; loading: boolean; updatedAt: Date | null }>({
    data: null,
    error: null,
    loading: path !== null,
    updatedAt: null,
  });
  const [nonce, setNonce] = useState(0);
  const lastPath = useRef(path);

  useEffect(() => {
    if (path === null) return;
    const ctl = new AbortController();
    const pathChanged = lastPath.current !== path;
    lastPath.current = path;
    setState((s) => ({ ...s, loading: true, ...(pathChanged ? { error: null } : {}) }));
    adminGet<T>(path, ctl.signal).then(
      (data) => setState({ data, error: null, loading: false, updatedAt: new Date() }),
      (err: unknown) => {
        if (ctl.signal.aborted) return;
        const error = err instanceof AdminApiError ? err : new AdminApiError(`/api/admin/${path}`, 0, String(err), null, new Date());
        setState((s) => ({ ...s, error, loading: false }));
      },
    );
    return () => ctl.abort();
  }, [path, nonce]);

  useEffect(() => {
    if (!pollMs || path === null) return;
    const id = window.setInterval(() => setNonce((n) => n + 1), pollMs);
    return () => window.clearInterval(id);
  }, [pollMs, path]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** POST JSON to the admin API; errors carry the API's message. */
export async function adminPost<T>(path: string, body: unknown): Promise<T> {
  const route = `/api/admin/${path}`;
  let res: Response;
  try {
    res = await fetch(route, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AdminApiError(route, 0, "The admin API couldn't be reached.", null, new Date());
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = (await res.json()) as { error?: unknown };
      if (typeof data.error === "string") message = data.error;
    } catch {
      /* not JSON */
    }
    throw new AdminApiError(route, res.status, message, res.headers.get("cf-ray"), new Date());
  }
  return (await res.json()) as T;
}
